import { Component, ErrorInfo, ReactNode } from 'react';
import { gameLogger } from '../../../utils/GameLogger';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown instead of the subtree; a function also gets a retry that renders the subtree again. */
  fallback?: ReactNode | ((retry: () => void) => ReactNode);
  /** Optional name for logging which boundary caught the error */
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Prevents a single UI panel crash from taking down the entire overlay.
 * Catches render errors in its subtree and displays a recoverable fallback.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    gameLogger.error('ui', `❌ ErrorBoundary${this.props.name ? ` [${this.props.name}]` : ''} caught:`, {
      error: error.message,
      stack: errorInfo.componentStack
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') return fallback(this.handleRetry);
      if (fallback) {
        return fallback;
      }
      return (
        <div style={{
          padding: '12px 16px',
          color: '#ff4757',
          background: 'rgba(255, 71, 87, 0.08)',
          border: '1px solid rgba(255, 71, 87, 0.2)',
          borderRadius: '8px',
          margin: '8px',
          fontFamily: "'Inter', sans-serif",
          fontSize: '12px',
          pointerEvents: 'auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px' }}>⚠️</span>
            <strong>UI Panel Error</strong>
          </div>
          <p style={{ margin: '0 0 8px 0', color: 'rgba(255, 71, 87, 0.7)', fontSize: '11px' }}>
            {this.state.error?.message}
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              cursor: 'pointer',
              color: '#fff',
              background: 'rgba(255, 71, 87, 0.6)',
              border: 'none',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '11px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255, 71, 87, 0.8)')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255, 71, 87, 0.6)')}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
